// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {Fragment, useState} from 'react';
import {Editor} from '../../../shared/editor/editor';
import {Button} from '../../../shared/button';
import {get_img_url} from './utils';
import styles from './bounty_reward_tiers.module.less';

const check_src = 'bounty_big_check.svg';

export interface i_bounty_reward_tier {
  title: string;
  subtitle?: string;
  items?: string[];
  featured?: boolean;
}

export interface i_bounty_reward_tiers_props {
  title?: string;
  description?: string;
  tiers?: i_bounty_reward_tier[];
  footnotes?: string[];
}

export const Bounty_reward_tiers = ({
  title,
  description,
  tiers=[],
  footnotes=[],
}: i_bounty_reward_tiers_props)=>{
  return <section className={styles.bounty_reward_tiers}>
    <div className={styles.bounty_reward_tiers__inner}>
      <div className={styles.bounty_reward_tiers__heading}>
        {title&&<h3 className={styles.bounty_reward_tiers__title}>
          {title}
        </h3>}
        {description&&<p
          className={styles.bounty_reward_tiers__description}>
          {description}
        </p>}
      </div>
      {!!tiers.length&&<div className={styles.bounty_reward_tiers__list}>
        {tiers.map((tier, index)=><article
          key={`${tier.title}-${index}`}
          className={styles.bounty_reward_tiers__item}
          data-featured={tier.featured||undefined}>
          <h3 className={styles.bounty_reward_tiers__item_title}>
            {tier.subtitle&&<span
              className={styles.bounty_reward_tiers__item_subtitle}>
              {tier.subtitle}
            </span>}
            <span className={styles.bounty_reward_tiers__amount}>
              {tier.title}
            </span>
          </h3>
          {!!tier.items?.length&&<ul
            className={styles.bounty_reward_tiers__features}>
            {tier.items.map((item, item_index)=><li
              key={`${item}-${item_index}`}
              className={styles.bounty_reward_tiers__feature}>
              <img
                className={styles.bounty_reward_tiers__icon}
                src={get_img_url(check_src)}
                alt=""
                aria-hidden={true}
              />
              <span className={styles.bounty_reward_tiers__feature_text}>
                {item}
              </span>
            </li>)}
          </ul>}
        </article>)}
      </div>}

      {!!footnotes.length&&<div
        className={styles.bounty_reward_tiers__footnotes}>
        {footnotes.map((footnote, index)=><p
          key={`${footnote}-${index}`}
          className={styles.bounty_reward_tiers__footnote}>
          {footnote}
        </p>)}
      </div>}
    </div>
  </section>;
};

interface i_bounty_reward_tiers_editor_props {
  value: i_bounty_reward_tiers_props;
  on_change: (value: i_bounty_reward_tiers_props)=>void;
  on_cancel?: ()=>void;
}

export const Bounty_reward_tiers_editor = (
  props: i_bounty_reward_tiers_editor_props
)=>{
  const [state, set_state] = useState<i_bounty_reward_tiers_props>({
    ...props.value,
    tiers: props.value?.tiers||[],
    footnotes: props.value?.footnotes||[],
  });
  const tiers = state.tiers||[];
  const footnotes = state.footnotes||[];
  const update_tier = (index: number, tier: i_bounty_reward_tier)=>{
    const next_tiers = [...tiers];
    next_tiers[index] = tier;
    set_state({...state, tiers: next_tiers});
  };
  const update_tier_item = (
    tier_index: number,
    item_index: number,
    value: string
  )=>
  {
    const next_tiers = [...tiers];
    const current_tier = next_tiers[tier_index];
    const items = current_tier.items
      ? [...current_tier.items]
      : [];
    items[item_index] = value;
    next_tiers[tier_index] = {
      ...current_tier,
      items,
    };
    set_state({...state, tiers: next_tiers});
  };

  return <Editor<i_bounty_reward_tiers_props>
    value={state}
    editors={[
      {key: 'title', label: 'Title', type: 'text'},
      {key: 'description', label: 'Description', type: 'textarea'},
    ]}
    on_change={set_state}
    on_save={()=>props.on_change(state)}
    on_cancel={props.on_cancel}>
    <Fragment>
      <h4>Reward tiers</h4>
      {tiers.map((tier, tier_index)=><div key={tier_index}>
        <Editor<i_bounty_reward_tier>
          title={tier.title||`Tier ${tier_index+1}`}
          value={tier}
          editors={[
            {key: 'subtitle', label: 'Prefix', type: 'text'},
            {key: 'title', label: 'Amount', type: 'text'},
            {key: 'featured', label: 'Featured', type: 'boolean'},
          ]}
          on_change={(value: i_bounty_reward_tier)=>
            update_tier(tier_index, value)}>
          <Fragment>
            <h5>Vulnerabilities</h5>

            {tier.items?.map((item, item_index)=><div
              key={item_index}>
              <Editor<{text: string}>
                value={{text: item}}
                editors={[
                  {key: 'text', label: 'Item', type: 'text'},
                ]}
                on_change={(value: {text: string})=>
                  update_tier_item(
                    tier_index,
                    item_index,
                    value.text
                  )}
              />
            </div>)}
          </Fragment>
        </Editor>
        <Button
          caption="Delete tier"
          size="xs"
          color="secondary"
          on_click={()=>set_state({
            ...state,
            tiers: tiers.filter((_, index)=>index!=tier_index),
          })}
        />
        <hr/>
      </div>)}
      <Button
        caption="Add tier"
        size="xs"
        on_click={()=>set_state({
          ...state,
          tiers: [
            ...tiers,
            {
              title: '',
              subtitle: '',
              items: [],
              featured: false,
            },
          ],
        })}
      />
      <h4>Footnotes</h4>
      {footnotes.map((footnote, index)=><div key={index}>
        <Editor<{text: string}>
          value={{text: footnote}}
          editors={[
            {key: 'text', label: 'Footnote', type: 'textarea'},
          ]}
          on_change={(value: {text: string})=>{
            const next_footnotes = [...footnotes];
            next_footnotes[index] = value.text;
            set_state({...state, footnotes: next_footnotes});
          }}
        />
        <Button
          caption="Delete footnote"
          size="xs"
          color="secondary"
          on_click={()=>set_state({
            ...state,
            footnotes: footnotes.filter((_, i)=>i!=index),
          })}
        />
      </div>)}
      <Button
        caption="Add footnote"
        size="xs"
        on_click={()=>set_state({
          ...state,
          footnotes: [...footnotes, ''],
        })}
      />
    </Fragment>
  </Editor>;
};
